/**
 * Operator compliance preferences — persisted client-side, enforced server-side.
 * Watermark/metadata locks for synthetic-media (deepfake-adjacent) workflows.
 */

export type SafetyLevel = 'off' | 'standard' | 'strict';
export type AgeAttestation = '16_plus' | '18_plus' | null;

export interface OperatorPrefs {
  safetyLevel: SafetyLevel;
  watermark: boolean;
  aiMetadata: boolean;
  dataConsent: boolean;
  ageAttestation: AgeAttestation;
  euDisclosureAcknowledged: boolean;
}

const STORAGE_KEY = 'pixlie_operator_prefs_v1';

const SYNTHETIC_MEDIA_WORKFLOWS = new Set([
  'i2v',
  'i2i',
  'fun_camera',
  'vace',
  'character_sheet',
  'tts_chatterbox',
]);

const LICENSE_BLOCKED_WORKFLOWS = new Set(['turbovision']);

export const DEFAULT_OPERATOR_PREFS: OperatorPrefs = {
  safetyLevel: 'strict',
  watermark: true,
  aiMetadata: true,
  dataConsent: true,
  ageAttestation: null,
  euDisclosureAcknowledged: false,
};

export function loadOperatorPrefs(): OperatorPrefs {
  if (typeof window === 'undefined') return { ...DEFAULT_OPERATOR_PREFS };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_OPERATOR_PREFS };
    const parsed = JSON.parse(raw) as Partial<OperatorPrefs>;
    return {
      ...DEFAULT_OPERATOR_PREFS,
      ...parsed,
      safetyLevel: parsed.safetyLevel ?? DEFAULT_OPERATOR_PREFS.safetyLevel,
    };
  } catch {
    return { ...DEFAULT_OPERATOR_PREFS };
  }
}

export function saveOperatorPrefs(prefs: OperatorPrefs): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

export function isSyntheticMediaWorkflow(workflowType: string): boolean {
  return SYNTHETIC_MEDIA_WORKFLOWS.has(workflowType);
}

export function isLicenseBlockedWorkflow(workflowType: string): boolean {
  return LICENSE_BLOCKED_WORKFLOWS.has(workflowType);
}

export function requiredAgeAttestation(level: SafetyLevel): AgeAttestation {
  if (level === 'off') return '18_plus';
  if (level === 'standard') return '16_plus';
  return null;
}

export function canUseSafetyLevel(level: SafetyLevel, attestation: AgeAttestation): boolean {
  const required = requiredAgeAttestation(level);
  if (!required) return true;
  return attestation === required;
}

/** Fields merged into every POST /api/generate body. */
export function buildGenerateComplianceFields(workflowType: string): {
  embedWatermark: boolean;
  includeAiMetadata: boolean;
  safetyLevel: SafetyLevel;
  modelTrainingConsent: boolean;
  ageAttestation?: AgeAttestation;
} {
  const prefs = loadOperatorPrefs();
  const synthetic = isSyntheticMediaWorkflow(workflowType);
  const attestation = prefs.ageAttestation;

  return {
    embedWatermark: synthetic ? true : prefs.watermark,
    includeAiMetadata: synthetic ? true : prefs.aiMetadata,
    safetyLevel: prefs.safetyLevel,
    modelTrainingConsent: prefs.dataConsent,
    ageAttestation: attestation ?? undefined,
  };
}

export function watermarkLockedForWorkflow(workflowType: string): boolean {
  return isSyntheticMediaWorkflow(workflowType);
}

export function metadataLockedForWorkflow(workflowType: string): boolean {
  return isSyntheticMediaWorkflow(workflowType);
}
